import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import { toast } from "react-toastify";
import type { FacilityNetworkViewModel } from "@utils/facilityNetworkView";
import type { MyFacilityListRow } from "types/myFacilityListRow";
import FacilityNetworkLeaveNetworkDialog from "./FacilityNetworkLeaveNetworkDialog";
import FacilityNetworkChildrenToolbar from "./FacilityNetworkChildrenToolbar";
import FacilityNetworkChildrenTable, {
  LINKED_FACILITY_DEFAULT_ROWS_PER_PAGE,
} from "./FacilityNetworkChildrenTable";
import { createUnlinkRequest } from "@services/facilityNetworkUnlink";
import {
  listChildLinkRequests,
  listLinkedFacilities,
} from "@services/facilityNetworkLink";
import { clearFacilityNetworkListCache } from "@services/facilityNetworkListFetch";
import { npiLookupService } from "@services/npiLookup";
import { getSelectedCountryCode } from "@utils/countryUtils";
import { mapNpiResultToSearchHit } from "./addChildFacilitySearchUtils";
import { getNetworkLinkListFacilityId } from "./facilityNetworkHierarchyUtils";
import {
  filterLinkedFacilityRows,
  mapLinkedFacilityApiRows,
  mapOutgoingLinkRequestsToLinkedRows,
  mergeLinkedFacilityRoster,
  enrichLinkedFacilityRows,
  applyNpiSearchHitsToRows,
  isRegistryPlaceholderName,
  sortLinkedFacilityRows,
  linkedFacilityRowToDashboardFacility,
  listTypeOptionsFromRows,
  type LinkedFacilityNpiHit,
  type LinkedFacilityRow,
  type LinkedFacilitySortKey,
} from "./facilityNetworkChildrenUtils";
import { FN } from "./facilityNetworkTheme";

type Props = {
  networkView: FacilityNetworkViewModel;
  onAddChildFacilities?: () => void;
  initialTypeFilter?: string;
  /** Open child individual dashboard inside Facility Network (not My Facility). */
  onViewChildDashboard?: (facility: MyFacilityListRow) => void;
};

async function fillMissingTypeLocationFromNpi(
  rows: LinkedFacilityRow[],
): Promise<LinkedFacilityRow[]> {
  const missing = rows.filter((row) => {
    if (row.npiDigits.length < 10) return false;
    return (
      row.typeLabel === "—" ||
      row.location === "—" ||
      !row.ccnDigits ||
      isRegistryPlaceholderName(row.name, row.npiDigits)
    );
  });
  if (missing.length === 0) return rows;
  const countryCode = getSelectedCountryCode();
  const hitsByNpi: Record<string, LinkedFacilityNpiHit> = {};
  await Promise.all(
    missing.map(async (row) => {
      const npi = row.npiDigits.slice(-10);
      try {
        const response = await npiLookupService.lookupNPI({
          npi_number: npi,
          country_code: countryCode,
        });
        const result = response.data?.results?.[0];
        if (!result) return;
        const hit = mapNpiResultToSearchHit(result);
        if (!hit) return;
        hitsByNpi[npi] = {
          city: hit.city,
          state: hit.state,
          ...(hit.name ? { name: hit.name } : {}),
          ...(hit.facilityType ? { facilityType: hit.facilityType } : {}),
          ...(hit.ccn ? { ccn: hit.ccn } : {}),
        };
      } catch {
        return;
      }
    }),
  );
  return applyNpiSearchHitsToRows(rows, hitsByNpi);
}

export default function FacilityNetworkChildrenTab({
  networkView,
  onAddChildFacilities,
  initialTypeFilter = "all",
  onViewChildDashboard,
}: Props) {
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState(initialTypeFilter);
  const [statusFilter, setStatusFilter] = useState("all");

  useEffect(() => {
    setTypeFilter(initialTypeFilter || "all");
  }, [initialTypeFilter]);
  const [rows, setRows] = useState<LinkedFacilityRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [sortKey, setSortKey] = useState<LinkedFacilitySortKey>("penalty");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(
    LINKED_FACILITY_DEFAULT_ROWS_PER_PAGE,
  );

  const parentId = getNetworkLinkListFacilityId(networkView);

  const fetchRows = useCallback(async () => {
    if (parentId == null) {
      setRows([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    clearFacilityNetworkListCache();
    try {
      const [apiRows, requests] = await Promise.all([
        listLinkedFacilities(parentId),
        listChildLinkRequests(parentId),
      ]);
      const linked = enrichLinkedFacilityRows(
        mapLinkedFacilityApiRows(apiRows),
        networkView.members,
      );
      const merged = mergeLinkedFacilityRoster(
        linked,
        mapOutgoingLinkRequestsToLinkedRows(requests),
      );
      const nextRows = await fillMissingTypeLocationFromNpi(merged);
      setRows(nextRows);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [parentId, networkView.members]);

  useEffect(() => {
    void fetchRows();
  }, [fetchRows]);

  useEffect(() => {
    setPage(1);
  }, [search, typeFilter, statusFilter, sortKey, sortDir, rowsPerPage]);

  const filteredRows = useMemo(() => {
    const filtered = filterLinkedFacilityRows(
      rows,
      search,
      typeFilter,
      statusFilter,
    );
    return sortLinkedFacilityRows(filtered, sortKey, sortDir);
  }, [rows, search, typeFilter, statusFilter, sortKey, sortDir]);

  const typeOptions = useMemo(
    () => listTypeOptionsFromRows(rows, typeFilter),
    [rows, typeFilter],
  );

  const handleSortChange = (key: LinkedFacilitySortKey) => {
    if (key === sortKey) {
      setSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
      return;
    }
    setSortKey(key);
    setSortDir(key === "penalty" ? "desc" : "asc");
  };

  const handleViewDashboard = (facilityId: number) => {
    const row = rows.find((item) => item.facilityId === facilityId);
    if (!row) return;
    const dashboardFacility = linkedFacilityRowToDashboardFacility(row);
    if (!dashboardFacility) {
      toast.error("Unable to open dashboard for this facility.");
      return;
    }
    onViewChildDashboard?.(dashboardFacility);
  };

  const [unlinkChild, setUnlinkChild] = useState<{ id: number; name: string } | null>(null);
  const [submittingUnlink, setSubmittingUnlink] = useState(false);

  const handleOpenUnlink = (childFacilityId: number) => {
    const childRow = rows.find((r) => r.facilityId === childFacilityId);
    setUnlinkChild({
      id: childFacilityId,
      name: childRow?.name || "child facility",
    });
  };

  const handleConfirmUnlink = async (reason: string) => {
    if (parentId == null || !unlinkChild) {
      toast.error("Unable to determine the parent facility for this unlink request.");
      return;
    }
    setSubmittingUnlink(true);
    try {
      await createUnlinkRequest(parentId, {
        counterpartyFacilityId: unlinkChild.id,
        reason: reason || "Parent requested removal from facility network",
      });
      toast.success("Unlink request sent. The child facility must approve it.");
      setUnlinkChild(null);
      void fetchRows();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to send unlink request.");
    } finally {
      setSubmittingUnlink(false);
    }
  };

  const clearFilters = () => {
    setSearch("");
    setTypeFilter("all");
    setStatusFilter("all");
  };

  const emptyPanelSx = {
    flex: 1,
    p: FN.panelPadding * 2,
    border: `1px solid ${FN.panelBorder}`,
    borderRadius: "8px",
    bgcolor: "#fff",
    display: "flex",
    flexDirection: "column" as const,
    justifyContent: "center",
    alignItems: "center",
    textAlign: "center" as const,
    minHeight: 280,
  };

  return (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        gap: 1.5,
        minHeight: 0,
      }}
    >
      <FacilityNetworkChildrenToolbar
        search={search}
        onSearchChange={setSearch}
        typeFilter={typeFilter}
        onTypeFilterChange={setTypeFilter}
        typeOptions={typeOptions}
        statusFilter={statusFilter}
        onStatusFilterChange={setStatusFilter}
      />

      {loading ? (
        <FacilityNetworkChildrenTable
          rows={[]}
          filteredCount={0}
          sortKey={sortKey}
          sortDir={sortDir}
          onSortChange={handleSortChange}
          page={1}
          rowsPerPage={rowsPerPage}
          onPageChange={setPage}
          onRowsPerPageChange={setRowsPerPage}
          loading
        />
      ) : rows.length === 0 ? (
        <Box sx={emptyPanelSx}>
          <Typography sx={{ fontSize: 14, color: FN.muted, mb: 1.5 }}>
            No linked facilities yet
          </Typography>
          {onAddChildFacilities && parentId != null ? (
            <Button
              variant="outlined"
              size="small"
              onClick={onAddChildFacilities}
              sx={{
                textTransform: "none",
                borderColor: FN.primary,
                color: FN.primary,
                fontSize: 13,
                fontFamily: FN.fontFamily,
              }}
            >
              Add child facilities
            </Button>
          ) : null}
        </Box>
      ) : filteredRows.length === 0 ? (
        <Box sx={emptyPanelSx}>
          <Typography sx={{ fontSize: 14, color: FN.muted, mb: 1.5 }}>
            No facilities match your filter
          </Typography>
          <Button
            variant="outlined"
            size="small"
            onClick={clearFilters}
            sx={{
              textTransform: "none",
              borderColor: FN.primary,
              color: FN.primary,
              fontSize: 13,
              fontFamily: FN.fontFamily,
            }}
          >
            Clear filter
          </Button>
        </Box>
      ) : (
        <FacilityNetworkChildrenTable
          rows={filteredRows}
          filteredCount={filteredRows.length}
          sortKey={sortKey}
          sortDir={sortDir}
          onSortChange={handleSortChange}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={setPage}
          onRowsPerPageChange={setRowsPerPage}
          onViewDashboard={handleViewDashboard}
          onRemove={handleOpenUnlink}
        />
      )}

      <FacilityNetworkLeaveNetworkDialog
        open={Boolean(unlinkChild)}
        facilityName={unlinkChild?.name || "child facility"}
        facilityRole="child"
        title="Request to unlink child facility"
        submitting={submittingUnlink}
        onClose={() => {
          if (!submittingUnlink) setUnlinkChild(null);
        }}
        onConfirm={(reason) => {
          void handleConfirmUnlink(reason);
        }}
      />
    </Box>
  );
}
